/**
 * Barcode scanner input helpers + scan-vs-type classification for product search.
 *
 * Scanners (keyboard wedge) emit characters very quickly; cashiers type slowly.
 * Typing must preserve spaces (e.g. "pochette bibo"). AZERTY→digit remap applies
 * only to scanner digit bursts, never to alphabetic product-name queries.
 *
 * IMPORTANT: AZERTY special keys must not form a regex character-class *range*
 * (a bare `-` between `(` and `è` matched letters and broke name typing).
 */

const AZERTY_DIGIT_REMAP: Record<string, string> = {
  '&': '1',
  é: '2',
  '"': '3',
  "'": '4',
  '(': '5',
  '-': '6',
  è: '7',
  _: '8',
  ç: '9',
  à: '0',
};

/** Hyphen at end of class — not a range. */
const HAS_AZERTY_DIGIT = /[&é"'()è_çà-]/;
const AZERTY_OR_DIGIT_ONLY = /^[0-9&é"'()è_çà-]+$/;

/** Common retail barcode lengths (EAN-8, UPC-A, EAN-13, ITF-14). */
const PREFERRED_BARCODE_LENGTHS = [13, 12, 8, 14];

/** Typical USB wedge inter-key delay is well under this (ms). */
export const SCAN_MAX_INTER_KEY_MS = 45;
/** Minimum length before we treat a fast burst as a barcode scan. */
export const SCAN_MIN_CHARS = 6;
/** Ignore a second add of the same code within this window (scan Enter + debounce). */
export const SCAN_ADD_DEDUPE_MS = 750;

export interface BarcodeScanNormalizationOptions {
  minBurstLength?: number;
}

export type ProductSearchInputMode = 'scan' | 'type';

/**
 * If a digit string is an exact N× repetition of a barcode-sized unit, keep one unit.
 */
export function collapseRepeatedBarcodeInput(raw: string): string {
  const trimmed = raw.trim();
  if (!/^[0-9]+$/.test(trimmed) || trimmed.length < 16) {
    return raw;
  }

  for (const len of PREFERRED_BARCODE_LENGTHS) {
    if (trimmed.length < len * 2 || trimmed.length % len !== 0) continue;
    const unit = trimmed.slice(0, len);
    if (unit.repeat(trimmed.length / len) === trimmed) {
      return unit;
    }
  }

  for (let len = Math.floor(trimmed.length / 2); len >= 8; len -= 1) {
    if (trimmed.length % len !== 0) continue;
    const unit = trimmed.slice(0, len);
    if (unit.repeat(trimmed.length / len) === trimmed) {
      return unit;
    }
  }

  return raw;
}

/**
 * True when value is only digits / AZERTY digit-keys (scanner burst), not letters/spaces.
 */
export function isAzertyDigitBurst(raw: string, minBurstLength = 8): boolean {
  const trimmed = raw.trim();
  if (trimmed.length < minBurstLength) return false;
  if (!HAS_AZERTY_DIGIT.test(trimmed)) return false;
  return AZERTY_OR_DIGIT_ONLY.test(trimmed);
}

/**
 * Converts AZERTY-looking scanner digit bursts to digits.
 * Leaves normal typing (names with spaces, letters) untouched — including trailing spaces.
 */
export function normalizeBarcodeScanInput(
  raw: string,
  options: BarcodeScanNormalizationOptions = {},
): string {
  const minBurstLength = options.minBurstLength ?? 8;
  if (!raw) return raw;

  if (isAzertyDigitBurst(raw, minBurstLength)) {
    const remapped = raw
      .trim()
      .split('')
      .map((char) => AZERTY_DIGIT_REMAP[char] ?? char)
      .join('');
    return collapseRepeatedBarcodeInput(remapped);
  }

  const digitsOnly = raw.trim();
  if (/^[0-9]+$/.test(digitsOnly) && digitsOnly.length >= 8) {
    return collapseRepeatedBarcodeInput(digitsOnly);
  }

  // Typing (names, SKUs with letters, spaces): preserve exactly, including trailing space.
  return raw;
}

/**
 * When a complete barcode is already in the field and a new scan starts after an idle
 * gap, keep only the new characters (scanner append without clear).
 */
export function replaceBarcodeAfterIdle(
  previous: string,
  incoming: string,
  idleGapMs: number,
  lastInputAt: number,
  now: number = Date.now(),
): string {
  const prevNorm = normalizeBarcodeScanInput(previous).trim();
  const prevIsCompleteBarcode = /^[0-9]{8,}$/.test(prevNorm);
  if (!prevIsCompleteBarcode) return incoming;
  if (now - lastInputAt < idleGapMs) return incoming;
  if (incoming.length <= previous.length) return incoming;
  if (!incoming.startsWith(previous)) return incoming;
  return incoming.slice(previous.length);
}

/** Numeric barcode (EAN/UPC/ITF) after normalization. */
export function isNumericBarcodeQuery(query: string): boolean {
  return /^[0-9]{8,}$/.test(query.trim());
}

/**
 * Classify search field input as scanner burst vs human typing using inter-key timing.
 */
export function classifyProductSearchInput(
  value: string,
  keyAt: number[],
  now: number = Date.now(),
): ProductSearchInputMode {
  const trimmed = value.trim();
  if (!trimmed) return 'type';

  // Spaces / letters ⇒ typing (product name / description).
  if (/[A-Za-zÀ-ÿ]/.test(trimmed) || /\s/.test(value)) {
    return 'type';
  }

  const recent = keyAt.filter((t) => now - t < 500);
  if (trimmed.length >= SCAN_MIN_CHARS && recent.length >= 3) {
    const deltas: number[] = [];
    for (let i = 1; i < recent.length; i += 1) {
      deltas.push(recent[i] - recent[i - 1]);
    }
    const avg = deltas.reduce((a, b) => a + b, 0) / deltas.length;
    if (avg > SCAN_MAX_INTER_KEY_MS) {
      return 'type';
    }
    if (avg > 0 && avg <= SCAN_MAX_INTER_KEY_MS && trimmed.length >= SCAN_MIN_CHARS) {
      return 'scan';
    }
  }

  if (isNumericBarcodeQuery(trimmed) || isAzertyDigitBurst(trimmed)) {
    return 'scan';
  }

  return 'type';
}

export function pushKeystrokeTime(
  keyAt: number[],
  now: number = Date.now(),
  keep = 24,
): number[] {
  const next = [...keyAt, now];
  return next.length > keep ? next.slice(next.length - keep) : next;
}

/**
 * Prepare controlled-input value: remap scanner bursts only; preserve typed spaces.
 */
export function prepareProductSearchFieldValue(
  previous: string,
  incoming: string,
  meta: { keyAt: number[]; lastInputAt: number },
  now: number = Date.now(),
  idleGapMs = 280,
): { value: string; mode: ProductSearchInputMode; keyAt: number[] } {
  const keyAt = pushKeystrokeTime(meta.keyAt, now);
  const coalesced = replaceBarcodeAfterIdle(
    previous,
    incoming,
    idleGapMs,
    meta.lastInputAt,
    now,
  );
  const mode = classifyProductSearchInput(coalesced, keyAt, now);
  const value =
    mode === 'scan' ? normalizeBarcodeScanInput(coalesced) : coalesced;
  return { value, mode, keyAt };
}

/** Returns true if this code should be applied (not a duplicate of a recent add). */
export function shouldAcceptScanAdd(
  code: string,
  lastCode: string,
  lastAt: number,
  now: number = Date.now(),
  windowMs = SCAN_ADD_DEDUPE_MS,
): boolean {
  const c = code.trim();
  if (!c) return false;
  if (c === lastCode && now - lastAt < windowMs) return false;
  return true;
}
